import { createEffect, createSignal, onCleanup, onMount, Show } from 'solid-js'
import { A } from '@solidjs/router'
import Footer from '/web/shared/aifans/Footer'
import { AccountLayout } from '/web/shared/aifans/AccountLayout'
import { chatStore, toastStore, userStore } from '/web/store'
import { AIFansSubscription } from '/common/types/aifans-schema'
import { aifansSubscriptionApi } from '/web/store/data/aifans-subscription'
import { getModelUrl } from '/web/shared/aifans/lib/model-util'
import SubscriptionModal from './SubscriptionModal'
import CancellationModal from './CancellationModal'

export default function Subscriptions() {
  const [toggleOption, setToggleOption] = createSignal('all')
  const [showCancelModal, setShowCancelModal] = createSignal(false)
  const [showReactivateModal, setShowReactivateModal] = createSignal(false)
  const [cancelURL, setCancelURL] = createSignal<string>()
  const [reactivateURL, setReactivateURL] = createSignal<string>()
  const [cancellingId, setCancellingId] = createSignal<string>()
  const [showCancellationModal, setShowCancellationModal] = createSignal(false)
  const [cancellingCharId, setCancellingCharId] = createSignal<string>()
  const [tableData, setTableData] = createSignal(userStore().aifansSubscriptions || [])
  const [sortKey, setSortKey] = createSignal<keyof AIFansSubscription | null>(null)
  const [sortDirection, setSortDirection] = createSignal<'asc' | 'desc'>('asc')

  createEffect(async () => {
    await loadSubscriptions()
  })

  createEffect(async () => {
    if (toggleOption() === 'all') {
      filterData(userStore().aifansSubscriptions)
    } else if (toggleOption() === 'active') {
      filterData(userStore().aifansSubscriptions?.filter((sub) => sub.status === 'active'))
    } else if (toggleOption() === 'expired') {
      filterData(userStore().aifansSubscriptions?.filter((sub) => sub.status === 'cancelled'))
    }
  })

  onMount(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'subscription-cancelled') {
        setShowCancelModal(false)
        toastStore.success('Subscription cancelled!')
        loadSubscriptions()
      } else if (event.data.type === 'subscription-reactivated') {
        setShowReactivateModal(false)
        toastStore.success('Subscription reactivated!')
        loadSubscriptions()
      }
    }
    window.addEventListener('message', handleMessage)
    onCleanup(() => window.removeEventListener('message', handleMessage))
  })

  const loadSubscriptions = async () => {
    await userStore.loadAIFansSubscriptions()
    filterData(userStore().aifansSubscriptions)
    setToggleOption('all')
  }

  const filterData = (data: AIFansSubscription[] | undefined) => {
    if (data && sortKey()) {
      setTableData(getSortedData(data, sortKey()!, sortDirection()))
    } else if (data) {
      setTableData(data)
      sortData('startDate', 'desc')
    }
  }

  const characterFromId = (characterId: string) => {
    return chatStore().allChars.list.find((c) => c._id === characterId)
  }

  const cancelSubscription = async (characterId: string) => {
    setCancellingCharId(characterId)
    setShowCancellationModal(true)
  }

  const handleCancellationConfirm = async (reason: string) => {
    const characterId = cancellingCharId()
    if (characterId === undefined) return
    setCancellingId(characterId)
    try {
      const sub = tableData()?.find((s) => s.characterId === characterId)
      let res: { result?: { success: boolean } }
      if (sub?.scope === 'platform') {
        res = await aifansSubscriptionApi.cancelPlatformMembership(reason)
      } else {
        if (!characterId) return
        res = await aifansSubscriptionApi.createCancellationLink(characterId, reason)
      }
      if (res.result?.success) {
        await loadSubscriptions()
        toastStore.success('Subscription cancelled!')
      } else {
        throw new Error('Failed to cancel subscription with Epoch.')
      }
    } catch (error) {
      console.log(error)
      toastStore.error('There was a problem canceling your subscription. Please try again.')
    } finally {
      setCancellingId(undefined)
      setCancellingCharId(undefined)
      setShowCancellationModal(false)
    }
  }

  // #211: reactivation feature removed for now (the flow was erroring for members).
  // Entry points on this page — the "Renew" button and the "Try again" retry link —
  // are removed; this handler is preserved (commented) so it can be switched back on.
  // const reactivateSubscription = async (characterId: string) => {
  //   try {
  //     const res = await aifansSubscriptionApi.createReactivationLink(characterId)
  //     if (res.result?.redirectURL) {
  //       setReactivateURL(res.result.redirectURL)
  //       setShowReactivateModal(true)
  //     } else {
  //       throw new Error('No redirect URL received')
  //     }
  //   } catch (error) {
  //     console.log(error)
  //     toastStore.error(
  //       'There was a problem reactivating your subscription. If you have already finished reactivating your subscription you may need to wait up to 15 minutes for reactivation to be completed. If not please try again or contact support.'
  //     )
  //   }
  // }

  const sortData = (key: keyof AIFansSubscription, initialDirection?: 'asc' | 'desc') => {
    if (sortKey() === key) {
      setSortDirection(sortDirection() === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDirection(initialDirection || 'asc')
    }
    setTableData(getSortedData(tableData() ?? [], key, sortDirection()))
  }

  const getSortedData = (
    data: AIFansSubscription[],
    key: keyof AIFansSubscription,
    direction: 'asc' | 'desc'
  ) => {
    return [...data].sort((a, b) => {
      let aValue: any = a[key]
      let bValue: any = b[key]
      if (key === 'characterId') {
        aValue = characterFromId(a.characterId)?.name
        bValue = characterFromId(b.characterId)?.name
      }
      if (key === 'amount') {
        aValue = parseFloat(a.amount.toString())
        bValue = parseFloat(b.amount.toString())
        if (isNaN(aValue)) aValue = Infinity
        if (isNaN(bValue)) bValue = Infinity
      }
      if (key === 'startDate' || key === 'endDate') {
        aValue = new Date(aValue).getTime()
        bValue = new Date(bValue).getTime()
      }
      if (aValue < bValue) return direction === 'asc' ? -1 : 1
      if (aValue > bValue) return direction === 'asc' ? 1 : -1
      return 0
    })
  }

  const getStatus = (subscription: AIFansSubscription) => {
    switch (subscription.status) {
      case 'active':
        return {
          text: 'Active',
          style: 'bg-[rgba(45,198,83,0.15)] text-[#2dc653] border-[rgba(45,198,83,0.3)]',
        }
      case 'cancelled':
        return {
          text: 'Cancelled',
          style: 'bg-[rgba(255,100,100,0.12)] text-[#ff6b6b] border-[rgba(255,100,100,0.25)]',
        }
      case 'expired':
        return {
          text: 'Expired',
          style: 'bg-[rgba(255,100,100,0.12)] text-[#ff6b6b] border-[rgba(255,100,100,0.25)]',
        }
      case 'reactivating':
        // #211: reactivation removed for now — no "Try again" retry link.
        return {
          text: 'Reactivating...',
          style: 'bg-[rgba(255,215,0,0.12)] text-[#FFD700] border-[rgba(255,215,0,0.25)]',
        }
      case 'restarted':
        return {
          text: 'Restarted',
          style:
            'bg-[rgba(255,255,255,0.06)] text-[rgba(255,255,255,0.5)] border-[rgba(255,255,255,0.1)]',
        }
      default:
        return {
          text: 'Unknown',
          style:
            'bg-[rgba(255,255,255,0.06)] text-[rgba(255,255,255,0.5)] border-[rgba(255,255,255,0.1)]',
        }
    }
  }

  const filters = ['all', 'active', 'expired'] as const
  const thClass =
    'py-3.5 px-5 text-left font-rajdhani text-[11px] font-bold uppercase tracking-[1.4px] text-[rgba(255,255,255,0.5)] lg:text-[13px] lg:py-[18px]'
  const tdClass =
    'py-4 px-5 align-middle font-rajdhani text-[14px] text-[rgba(255,255,255,0.78)] lg:text-[16px] lg:py-5'

  return (
    <>
      <AccountLayout>
        <div class="mb-4 mt-3 font-bebas text-[28px] leading-none tracking-[1.5px] text-white sm:mb-5 sm:mt-0 sm:text-[38px] lg:text-[48px]">
          Subscriptions
        </div>

        {/* Filter tabs */}
        <div class="mb-5 flex gap-2.5">
          {filters.map((f) => (
            <button
              onClick={() => setToggleOption(f)}
              class={`inline-flex items-center justify-center rounded-full border px-4 py-2 font-rajdhani text-[13px] font-extrabold tracking-[0.5px] transition-all sm:px-6 sm:py-2.5 sm:text-[14px] lg:px-7 lg:py-3 lg:text-[15px] ${
                toggleOption() === f
                  ? 'border-[rgba(138,43,226,0.55)] bg-[rgba(138,43,226,0.22)] text-white shadow-[0_0_12px_rgba(138,43,226,0.2)]'
                  : 'border-[rgba(255,255,255,0.1)] bg-[rgba(255,255,255,0.04)] text-[rgba(255,255,255,0.45)] hover:border-[rgba(138,43,226,0.35)] hover:text-[rgba(255,255,255,0.7)]'
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        <div class="overflow-hidden sm:rounded-2xl sm:border sm:border-[rgba(138,43,226,0.15)] sm:bg-[rgba(16,12,30,0.6)]">
          <Show
            when={tableData()?.length > 0}
            fallback={
              <div class="py-16 text-center">
                <p class="font-bebas text-xl text-[rgba(255,255,255,0.4)]">
                  No subscriptions match this filter.
                </p>
              </div>
            }
          >
            {/* Desktop table */}
            <div class="hidden overflow-x-auto sm:block">
              <table class="w-full border-collapse">
                <thead>
                  <tr
                    style={{
                      background: 'rgba(138,43,226,0.08)',
                      'border-bottom': '1px solid rgba(255,255,255,0.08)',
                    }}
                  >
                    <th class={thClass}>Creator</th>
                    <th class={thClass}>Price</th>
                    <th class={thClass}>Started</th>
                    <th class={thClass}>Expires</th>
                    <th class={thClass}>Status</th>
                    <th class={thClass}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {tableData()?.map((item) => {
                    const char = characterFromId(item.characterId)
                    const status = getStatus(item)
                    return (
                      <tr
                        style={{ 'border-bottom': '1px solid rgba(255,255,255,0.06)' }}
                        class="transition-colors hover:bg-[rgba(138,43,226,0.05)]"
                      >
                        <td class={tdClass}>
                          <Show when={char}>
                            <A
                              href={getModelUrl(char!.categorySlug, char!.slug)}
                              class="hover:underline"
                            >
                              <div class="font-bebas text-[16px] leading-tight tracking-[0.5px] text-white lg:text-[19px] lg:tracking-[0.6px]">
                                {char!.name}
                              </div>
                              {char!.realName && (
                                <div class="mt-0.5 font-rajdhani text-[11px] font-semibold text-[rgba(255,255,255,0.35)] lg:mt-1 lg:text-[13px]">
                                  {char!.realName}
                                </div>
                              )}
                            </A>
                          </Show>
                        </td>
                        <td class={tdClass}>${item.amount}</td>
                        <td class={tdClass}>{formatDate(item.startDate)}</td>
                        <td class={tdClass}>{formatDate(item.endDate)}</td>
                        <td class={tdClass}>
                          <span
                            class={`inline-block rounded-md border px-3 py-1 font-rajdhani text-[10px] font-bold uppercase tracking-[0.8px] lg:px-3.5 lg:py-1.5 lg:text-[11px] ${status.style}`}
                          >
                            {status.text}
                          </span>
                        </td>
                        <td class={tdClass}>
                          <Show when={status.text === 'Active'}>
                            <button
                              class="rounded-lg border border-[rgba(255,100,100,0.25)] bg-[rgba(255,100,100,0.06)] px-3.5 py-1.5 font-rajdhani text-[11px] font-bold uppercase tracking-[0.5px] text-[rgba(255,100,100,0.75)] transition-colors hover:bg-[rgba(255,100,100,0.18)] hover:text-[#ff6b6b] lg:px-4 lg:py-2 lg:text-[13px]"
                              onClick={() => cancelSubscription(item.characterId)}
                              disabled={cancellingId() === item.characterId}
                            >
                              {cancellingId() === item.characterId ? 'Cancelling...' : 'Cancel'}
                            </button>
                          </Show>
                          {/* #211: reactivation "Renew" button removed for now */}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile stacked cards */}
            <div class="flex flex-col gap-3 sm:hidden">
              {tableData()?.map((item) => {
                const char = characterFromId(item.characterId)
                const status = getStatus(item)
                return (
                  <div
                    class="grid w-full grid-cols-2 gap-x-3.5 gap-y-2.5 rounded-[14px] border border-[rgba(138,43,226,0.25)] p-4"
                    style={{
                      background: 'rgba(16,12,30,0.85)',
                      'box-shadow': '0 4px 14px rgba(0,0,0,0.25)',
                    }}
                  >
                    {/* Creator — full width row */}
                    <div class="col-span-2 mb-1 border-b border-[rgba(255,255,255,0.06)] pb-2.5">
                      <Show when={char}>
                        <A
                          href={getModelUrl(char!.categorySlug, char!.slug)}
                          class="hover:underline"
                        >
                          <div class="font-bebas text-[16px] tracking-[0.5px] text-white">
                            {char!.name}
                          </div>
                          {char!.realName && (
                            <div class="mt-0.5 font-rajdhani text-[11px] font-semibold text-[rgba(255,255,255,0.35)]">
                              {char!.realName}
                            </div>
                          )}
                        </A>
                      </Show>
                    </div>

                    {/* Price */}
                    <div>
                      <div class="mb-0.5 font-rajdhani text-[9px] font-bold uppercase tracking-[1.2px] text-[rgba(255,255,255,0.45)]">
                        Price
                      </div>
                      <div class="font-rajdhani text-[13px] text-[rgba(255,255,255,0.78)]">
                        ${item.amount}
                      </div>
                    </div>

                    {/* Started */}
                    <div>
                      <div class="mb-0.5 font-rajdhani text-[9px] font-bold uppercase tracking-[1.2px] text-[rgba(255,255,255,0.45)]">
                        Started
                      </div>
                      <div class="font-rajdhani text-[13px] text-[rgba(255,255,255,0.78)]">
                        {formatDate(item.startDate)}
                      </div>
                    </div>

                    {/* Expires */}
                    <div>
                      <div class="mb-0.5 font-rajdhani text-[9px] font-bold uppercase tracking-[1.2px] text-[rgba(255,255,255,0.45)]">
                        Expires
                      </div>
                      <div class="font-rajdhani text-[13px] text-[rgba(255,255,255,0.78)]">
                        {formatDate(item.endDate)}
                      </div>
                    </div>

                    {/* Status */}
                    <div>
                      <div class="mb-0.5 font-rajdhani text-[9px] font-bold uppercase tracking-[1.2px] text-[rgba(255,255,255,0.45)]">
                        Status
                      </div>
                      <span
                        class={`inline-block rounded-md border px-2.5 py-0.5 font-rajdhani text-[10px] font-bold uppercase tracking-[0.8px] ${status.style}`}
                      >
                        {status.text}
                      </span>
                    </div>

                    {/* Action — full width row */}
                    <div class="col-span-2 pt-1.5">
                      <Show when={status.text === 'Active'}>
                        <button
                          class="w-full rounded-lg border border-[rgba(255,100,100,0.25)] bg-[rgba(255,100,100,0.06)] py-2.5 text-center font-rajdhani text-[11px] font-bold uppercase tracking-[0.5px] text-[rgba(255,100,100,0.75)] transition-colors hover:bg-[rgba(255,100,100,0.15)]"
                          onClick={() => cancelSubscription(item.characterId)}
                          disabled={cancellingId() === item.characterId}
                        >
                          {cancellingId() === item.characterId ? 'Cancelling...' : 'Cancel'}
                        </button>
                      </Show>
                      {/* #211: reactivation "Renew" button removed for now */}
                    </div>
                  </div>
                )
              })}
            </div>
          </Show>
        </div>
      </AccountLayout>
      <Footer />
      <SubscriptionModal
        show={showCancelModal() || showReactivateModal()}
        onClose={() => {
          setShowCancelModal(false)
          setCancelURL(undefined)
          setShowReactivateModal(false)
          setReactivateURL(undefined)
        }}
        redirectURL={cancelURL() || reactivateURL()}
      />
      <CancellationModal
        show={showCancellationModal()}
        onClose={() => {
          setShowCancellationModal(false)
          setCancellingCharId(undefined)
        }}
        onConfirm={handleCancellationConfirm}
        characterName={cancellingCharId() ? characterFromId(cancellingCharId()!)?.name : undefined}
        loading={cancellingId() === cancellingCharId()}
      />
    </>
  )
}

const formatDate = (dateString: string) => {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(new Date(dateString))
}
